import type { AccountTypeRecord, LenderApiResponse, LenderParentRecord } from '@/shared/api/services/lenderService';

// Normalizes strings for case-insensitive, trim-safe comparisons
export const normalize = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

// Blocked company keys (case-insensitive)
export const BLOCKED_COMPANY_KEYS = new Set<string>([
  'cab',
  'reach out',
  'cadent gas',
  'cadent gas network',
  'cadent gas network 123',
  'cadent gas network 456',
].map(normalize));

export interface LenderLookup {
  byParent: Map<string, LenderParentRecord>;
  childToParent: Map<string, { parent: LenderParentRecord; childRaw?: LenderApiResponse | null }>;
}

export interface SelectionContext {
  manual: boolean;
  parentRecord?: LenderParentRecord | null;
  selectedChildRaw?: LenderApiResponse | null;
  productOptions: Array<{ value: string; label: string }>;
  resolvedProductSelection: string;
  showProduct: boolean;
  productRequired: boolean;
  accountTypeOptions: AccountTypeRecord[];
  showAccountType: boolean;
  accountTypeRequired: boolean;
  autoSelectProduct?: string | null;
  autoSelectAccountType?: string | null;
  referencePlaceholder: string;
  referenceRequired: boolean;
  referenceVisible: boolean;
  effectiveCreditorName: string;
  effectiveLenderName: string | null;
  blocked: boolean;
  addToDebtList: boolean;
  showCompanyList: boolean;
}

export const DEFAULT_SELECTION_CONTEXT: SelectionContext = {
  manual: false,
  parentRecord: null,
  selectedChildRaw: null,
  productOptions: [],
  resolvedProductSelection: '',
  showProduct: false,
  productRequired: false,
  accountTypeOptions: [],
  showAccountType: false,
  accountTypeRequired: false,
  autoSelectProduct: null,
  autoSelectAccountType: null,
  referencePlaceholder: 'Enter your account number',
  referenceRequired: false,
  referenceVisible: false,
  effectiveCreditorName: '',
  effectiveLenderName: null,
  blocked: false,
  addToDebtList: false,
  showCompanyList: false,
};

export const buildLenderLookup = (lenders: LenderParentRecord[]): LenderLookup => {
  const byParent = new Map<string, LenderParentRecord>();
  const childToParent = new Map<string, { parent: LenderParentRecord; childRaw?: LenderApiResponse }>();

  lenders.forEach((parent) => {
    const parentKey = normalize(parent.parentCompany);
    if (parentKey) {
      byParent.set(parentKey, parent);
    }

    const childNames = Array.isArray(parent.childCompany) ? parent.childCompany : [];
    const rawChildren = Array.isArray(parent.childrenRaw) ? parent.childrenRaw : [];

    childNames.forEach((childName) => {
      const childKey = normalize(childName);
      if (!childKey) {
        return;
      }
      const matchedRaw = rawChildren.find((child) => normalize(child.lender ?? child.description ?? '') === childKey);
      if (matchedRaw) {
        childToParent.set(childKey, { parent, childRaw: matchedRaw });
      } else {
        childToParent.set(childKey, { parent });
      }
    });

    rawChildren.forEach((child) => {
      const childKey = normalize(child.lender ?? child.description ?? '');
      if (!childKey) {
        return;
      }
      if (!childToParent.has(childKey)) {
        childToParent.set(childKey, { parent, childRaw: child });
      }
    });
  });

  return { byParent, childToParent };
};

export const computeSelectionContext = (params: {
  companySelection: string;
  productSelection: string;
  manualCompanyName: string;
  lenders: LenderParentRecord[];
  accountTypes: AccountTypeRecord[];
  lookup?: LenderLookup;
  manual?: boolean;
}): SelectionContext => {
  const {
    companySelection,
    productSelection,
    manualCompanyName,
    lenders,
    accountTypes,
    lookup,
    manual = false,
  } = params;

  const safeLookup = lookup ?? buildLenderLookup(lenders);

  const noCompanySelected = !companySelection || companySelection.trim().length === 0;
  if (noCompanySelected) {
    return {
      ...DEFAULT_SELECTION_CONTEXT,
      manual: false,
      effectiveCreditorName: manualCompanyName.trim(),
    };
  }

  if (manual) {
    const trimmedManual = manualCompanyName.trim();
    const uniqueCodes = new Set(
      accountTypes
        .map((item) => item.code?.trim())
        .filter((code): code is string => Boolean(code && code.length > 0))
    );
    const autoAccountTypeValue = uniqueCodes.size === 1 ? (Array.from(uniqueCodes.values())[0] ?? null) : null;

    return {
      manual: true,
      parentRecord: null,
      selectedChildRaw: null,
      productOptions: [],
      resolvedProductSelection: '',
      showProduct: false,
      productRequired: false,
      accountTypeOptions: accountTypes,
      showAccountType: accountTypes.length > 0, // visible when options exist
      accountTypeRequired: accountTypes.length > 0,
      autoSelectProduct: null,
      autoSelectAccountType: autoAccountTypeValue,
      referencePlaceholder: 'Enter your account number',
      referenceRequired: Boolean(trimmedManual.length),
      referenceVisible: Boolean(trimmedManual.length),
      effectiveCreditorName: trimmedManual,
      effectiveLenderName: null,
      blocked: false,
      addToDebtList: true,
      showCompanyList: true,
    };
  }

  const parentRecord = safeLookup.byParent.get(normalize(companySelection));
  if (!parentRecord) {
    // Fallback to manual-like flow when company not found in lookup
    const fallbackManual = companySelection.trim();
    const uniqueCodes = new Set(
      accountTypes
        .map((item) => item.code?.trim())
        .filter((code): code is string => Boolean(code && code.length > 0))
    );
    const autoAccountTypeValue = uniqueCodes.size === 1 ? (Array.from(uniqueCodes.values())[0] ?? null) : null;

    return {
      manual: true,
      parentRecord: null,
      selectedChildRaw: null,
      productOptions: [],
      resolvedProductSelection: '',
      showProduct: false,
      productRequired: false,
      accountTypeOptions: accountTypes,
      showAccountType: accountTypes.length > 0,
      accountTypeRequired: accountTypes.length > 0,
      autoSelectProduct: null,
      autoSelectAccountType: autoAccountTypeValue,
      referencePlaceholder: 'Enter your account number',
      referenceRequired: Boolean(fallbackManual.length),
      referenceVisible: Boolean(fallbackManual.length),
      effectiveCreditorName: fallbackManual,
      effectiveLenderName: null,
      blocked: false,
      addToDebtList: true,
      showCompanyList: true,
    };
  }

  const childOptions = Array.from(
    new Set(
      (parentRecord.childCompany ?? []).filter((child): child is string => Boolean(child && child.trim().length > 0))
    )
  ).map((child) => ({ value: child, label: child }));

  const validProductKeys = new Set(childOptions.map((option) => normalize(option.value)));
  const incomingProductKey = normalize(productSelection);
  const resolvedProductSelection = incomingProductKey && validProductKeys.has(incomingProductKey)
    ? productSelection
    : '';

  const autoSelectProduct = !resolvedProductSelection && childOptions.length === 1
    ? childOptions[0]?.value ?? null
    : null;

  const effectiveProductSelection = resolvedProductSelection || autoSelectProduct || '';

  const childRawRecords = Array.isArray(parentRecord.childrenRaw) ? parentRecord.childrenRaw : [];
  const effectiveChildRaw = effectiveProductSelection
    ? childRawRecords.find((child) => normalize(child.lender ?? child.description ?? '') === normalize(effectiveProductSelection))
    : undefined;

  const childShowCompanyList = effectiveChildRaw?.showCompanyList === 'Y';
  const childAddToDebtList = effectiveChildRaw?.addToDebtList === 'Y';
  const parentShowCompanyList = parentRecord.showCompanyList;
  const parentAddToDebtList = parentRecord.addToDebtList;

  const showCompanyList = childShowCompanyList || parentShowCompanyList;
  const addToDebtList = childAddToDebtList || parentAddToDebtList;
  // Hide product dropdown when only one option exists (auto-selected behind the scenes)
  const showProduct = childOptions.length > 1 && showCompanyList;
  const productRequired = childOptions.length > 0 && showCompanyList;

  const candidateNames = [
    effectiveChildRaw?.lender,
    effectiveChildRaw?.description,
    parentRecord.parentCompany,
    parentRecord.raw?.lender,
    parentRecord.raw?.parentCompany,
  ].filter((name): name is string => Boolean(name && name.trim().length > 0));

  const blocked = candidateNames.some((name) => BLOCKED_COMPANY_KEYS.has(normalize(name)));

  const referencePlaceholder = effectiveChildRaw?.referenceNumberFormat
    ?? parentRecord.referenceNumberFormat
    ?? 'Enter your account number';

  const referenceAllowed = addToDebtList && showCompanyList && !blocked;

  const accountTypeTargets = new Set(candidateNames.map(normalize));
  let resolvedAccountTypeOptions = accountTypes.filter((item) => {
    if (!item.companyName) return false;
    return accountTypeTargets.has(normalize(item.companyName));
  });

  // Fallback: if no company-specific options are found, expose the full list
  // so users can still select a valid account type (prevents API 500s).
  if (resolvedAccountTypeOptions.length === 0) {
    resolvedAccountTypeOptions = accountTypes;
  }

  const accountTypeRequired = addToDebtList && resolvedAccountTypeOptions.length > 0;
  const uniqueAccountCodes = new Set(
    resolvedAccountTypeOptions
      .map((item) => item.code?.trim())
      .filter((code): code is string => Boolean(code && code.length > 0))
  );
  const autoSelectAccountType = uniqueAccountCodes.size === 1 ? (Array.from(uniqueAccountCodes.values())[0] ?? null) : null;

  // Hide account-type control if a single option is available (auto-selected)
  const showAccountType = accountTypeRequired && uniqueAccountCodes.size > 1;

  const effectiveCreditorName = effectiveProductSelection || parentRecord.parentCompany;
  const effectiveLenderName = effectiveProductSelection || null;

  return {
    manual: false,
    parentRecord,
    selectedChildRaw: effectiveChildRaw ?? null,
    productOptions: childOptions,
    resolvedProductSelection: effectiveProductSelection,
    showProduct,
    productRequired,
    accountTypeOptions: resolvedAccountTypeOptions,
    showAccountType,
    accountTypeRequired,
    autoSelectProduct,
    autoSelectAccountType,
    referencePlaceholder,
    referenceRequired: referenceAllowed,
    referenceVisible: referenceAllowed,
    effectiveCreditorName,
    effectiveLenderName,
    blocked,
    addToDebtList,
    showCompanyList,
  };
};


